import { alpha, darken, lighten } from "@mui/material/styles";

const DEFAULT_COLOR = "#5a34d6";

const clampRadius = (value, fallback) => {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return fallback;
  }

  return value;
};

export function normalizeDimension(value, fallback, { pxByDefault = true } = {}) {
  if (value === undefined || value === null || value === "") {
    return fallback;
  }

  if (typeof value === "number") {
    return pxByDefault ? `${value}px` : `${value}`;
  }

  return value;
}

export function normalizeButtonWidth(value) {
  if (value === undefined || value === null || value === "") {
    return "100%";
  }

  if (typeof value === "number") {
    return `${value}%`;
  }

  return value;
}

export function resolveThemeColor(color, theme, fallback = DEFAULT_COLOR) {
  if (!color) {
    return fallback;
  }

  if (typeof color === "string" && color.startsWith("$")) {
    const paletteKey = color.slice(1);
    const paletteValue = theme?.palette?.[paletteKey];

    if (typeof paletteValue === "string") {
      return paletteValue;
    }

    if (paletteValue?.main) {
      return paletteValue.main;
    }

    return fallback;
  }

  return color;
}

const safeTransform = (transform, color, amount, fallback = DEFAULT_COLOR) => {
  try {
    return transform(color, amount);
  } catch (error) {
    return transform(fallback, amount);
  }
};

export function getChromePalette(color, options = {}) {
  const base = color || DEFAULT_COLOR;
  const outerDark = options.outerDark || "#11051f";
  const innerDark = options.innerDark || "#221037";
  const accent = options.accent || "#d166ff";
  const goldBright = options.goldBright || "#ffe88a";
  const goldMid = options.goldMid || "#ffbe45";
  const goldDark = options.goldDark || "#8d4b0f";

  return {
    base,
    outerDark,
    innerDark,
    accent,
    goldBright,
    goldMid,
    goldDark,
    fillTop: safeTransform(lighten, base, 0.2),
    fillMid: base,
    fillBottom: safeTransform(darken, base, 0.3),
    fillShadow: safeTransform(darken, base, 0.45),
    glow: alpha(safeTransform(lighten, base, 0.25), 0.55),
    accentGlow: alpha(accent, 0.45),
    topHighlight: alpha("#ffffff", 0.45),
    edgeShadow: alpha("#000000", 0.38),
    overlay: alpha(safeTransform(lighten, base, 0.1), 0.16),
    disabledBase: safeTransform(darken, base, 0.25),
  };
}

export function getContentTextShadow(disabled) {
  return disabled
    ? "none"
    : "-1.5px -1.5px 0 rgba(10, 3, 17, 0.92), 1.5px -1.5px 0 rgba(10, 3, 17, 0.92), -1.5px 1.5px 0 rgba(10, 3, 17, 0.92), 1.5px 1.5px 0 rgba(10, 3, 17, 0.92), 0 4px 12px rgba(0, 0, 0, 0.42)";
}

export function getWrapperFilter(disabled) {
  return disabled ? "grayscale(0.25) saturate(0.7) brightness(0.8)" : "none";
}

export function getIconMedallionPalette(color) {
  const palette = getChromePalette(color);

  return {
    ...palette,
    medallionTop: safeTransform(lighten, palette.base, 0.25),
    medallionBottom: safeTransform(darken, palette.base, 0.4),
  };
}

export function getCornerRadius(radius, fallback) {
  return clampRadius(radius, fallback);
}
